import { hrEsalary } from '@hr/main.jsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { Form, Image, Modal, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { buildAlert } from '../../../../actions/index.js'
import { getIntegrations } from '../../../../actions/organization/activeintegrations.js'
import {
	finagoTimeCredentialsSave,
	finagoTimeEventMappingsBatchPut,
	finagoTimeEventMappingsQuery,
	organizationPropertyDelete,
	organizationPropertyGet,
	organizationTestToken,
} from '../../../../api/index.js'
import { Button } from '../../../../common/Button.tsx'
import { Confirmation } from '../../../../common/confirmation.jsx'
import { authSelector, selectedOrganizationSelector } from '../../../../reducers/index.js'
import { FinagoTimeEventMapping } from './FinagoTimeEventMapping.jsx'
import { SettingsTimeContext, SettingsTimeContextProvider } from './SettingsTimeContext.jsx'

const key = 'credentialsFinagoTime'

function SettingsTimeModal({ setShow, active }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const auth = useSelector(authSelector)
	const selectedOrganization = useSelector(selectedOrganizationSelector)

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm()

	const formValues = watch()
	const [testResult, setTestResult] = useState(<br />)
	const [activeTab, setActiveTab] = useState('credentials')
	const orgId = selectedOrganization.businessIdentifier ?? auth.companyBusinessId

	useEffect(() => {
		async function fetchCredentials() {
			const res = await organizationPropertyGet(orgId, key)
			if (res?.status === 200 && res.data) {
				Object.keys(res.data).forEach((k) => {
					setValue(k, res.data[k])
				})
			}
		}
		fetchCredentials()
	}, [orgId, setValue])

	async function saveCredentials(data) {
		const payload = {
			username: data.username,
			password: data.password,
			integration_url: data.integration_url,
		}

		const res = await finagoTimeCredentialsSave(orgId, payload)

		if (res?.status === 200) {
			dispatch(getIntegrations(orgId))
			closeModal()
		}
	}

	async function deleteCredentials() {
		setTestResult(<br />)
		const res = await organizationPropertyDelete(orgId, key)

		if (res?.status === 200) {
			setValue('username', '')
			setValue('password', '')
			setValue('integration_url', '')
			dispatch(getIntegrations(orgId))
		}
	}

	async function testToken() {
		setTestResult(<Spinner size="sm" variant="primary" />)
		try {
			const res = await organizationTestToken('finago-time')
			if (res && res.status === 200) {
				switch (res.data) {
					case true:
						setTestResult(`${t('TokenValid', 'Token is valid')}`)
						break
					case false:
						setTestResult(`${t('TokenInvalid', 'Token is invalid')}`)
						break
					default:
						setTestResult(`${t('ApiError', 'API error')}`)
				}
			} else {
				setTestResult(`${t('ApiError', 'API error')}`)
			}
		} catch {
			setTestResult(`${t('ApiError', 'API error')}`)
		}
	}

	const closeModal = () => setShow()

	const isInvalid =
		!formValues?.username?.trim() || !formValues?.password?.trim() || !formValues?.integration_url?.trim()

	return (
		<SettingsTimeContextProvider>
			<SettingsTimeModalInner
				closeModal={closeModal}
				active={active}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				register={register}
				handleSubmit={handleSubmit}
				saveCredentials={saveCredentials}
				deleteCredentials={deleteCredentials}
				testToken={testToken}
				testResult={testResult}
				errors={errors}
				isInvalid={isInvalid}
				t={t}
			/>
		</SettingsTimeContextProvider>
	)
}

function SettingsTimeModalInner({
	closeModal,
	active,
	activeTab,
	setActiveTab,
	register,
	handleSubmit,
	saveCredentials,
	deleteCredentials,
	testToken,
	testResult,
	errors,
	isInvalid,
	t,
}) {
	const { editedCells, setEditedCells } = useContext(SettingsTimeContext)
	const dispatch = useDispatch()
	const queryClient = useQueryClient()

	const { mutate: saveMappings, isPending: isSaving } = useMutation({
		mutationFn: (dtos) => finagoTimeEventMappingsBatchPut(dtos),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: finagoTimeEventMappingsQuery.queryKey })
			setEditedCells([])
		},
		onError: (error) => {
			dispatch(buildAlert('ERROR', t('Error', 'Virhe'), error?.response?.data?.message ?? t('ApiError', 'API error')))
		},
	})

	return (
		<Modal show onHide={closeModal} size="xl">
			<Form onSubmit={handleSubmit(saveCredentials)}>
				<Modal.Header closeButton>
					<Modal.Title style={{ lineHeight: 2 }}>
						<Image src="/integrations/finagotime.svg" height={25} alt="finago time" /> {t('integration', 'integraatio')}
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<Tabs
						onSelect={(tab) => setActiveTab(tab)}
						activeKey={activeTab}
						defaultActiveKey="credentials"
						className="mb-3"
					>
						<Tab eventKey="credentials" title={t('Credentials', 'Tunnukset')}>
							<Form.Group className="mb-3">
								<Form.Label>{t('Username', 'Käyttäjänimi')}</Form.Label>
								<Form.Control {...register('username', { required: true })} type="text" isInvalid={errors.username} />
								<Form.Control.Feedback type="invalid">
									{t('UsernameIsRequired', 'Käyttäjä on pakollinen')}
								</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>{t('Password', 'Salasana')}</Form.Label>
								<Form.Control
									{...register('password', { required: true })}
									type="password"
									isInvalid={errors.password}
								/>
								<Form.Control.Feedback type="invalid">
									{t('PasswordIsRequired', 'Salasana on pakollinen')}
								</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>{t('IntegrationURL', 'Integraatio URL')}</Form.Label>
								<Form.Control
									{...register('integration_url', { required: true })}
									type="text"
									placeholder="example.address.fi"
									isInvalid={errors.integration_url}
								/>
								<Form.Control.Feedback type="invalid">
									{t('IntegrationURLIsRequired', 'URL on pakollinen')}
								</Form.Control.Feedback>
							</Form.Group>
							{testResult}
						</Tab>
						{window.location.hostname !== hrEsalary && active && (
							<Tab eventKey="event" title={t('EventMapping', 'Tapahtumakartoitus')}>
								<FinagoTimeEventMapping />
							</Tab>
						)}
					</Tabs>
				</Modal.Body>

				<Modal.Footer>
					{activeTab === 'credentials' && (
						<>
							{active && <Button onClick={testToken}>{t('TestConnection', 'Testaa yhteys')}</Button>}
							{active && (
								<Confirmation title="Poistetaanko Time-integraatio?" actionFunction={deleteCredentials}>
									<Button variant="warning">{t('Deactivate', 'Poista käytöstä')}</Button>
								</Confirmation>
							)}
							{!active && (
								<Confirmation title="Tallennetaanko Time-integraatio?" actionFunction={handleSubmit(saveCredentials)}>
									<Button variant="success" disabled={isInvalid}>
										{t('Activate', 'Ota käyttöön')}
									</Button>
								</Confirmation>
							)}
						</>
					)}
					{activeTab === 'event' && (
						<Button
							variant="primary"
							onClick={() => saveMappings(editedCells)}
							disabled={editedCells.length === 0 || isSaving}
							className="d-flex align-items-center gap-1"
						>
							{isSaving && <Spinner animation="border" role="status" variant="light" size="sm" />}
							{t('Save', 'Tallenna')}
						</Button>
					)}
				</Modal.Footer>
			</Form>
		</Modal>
	)
}

export function SettingsTime({ setShow, active }) {
	return <SettingsTimeModal setShow={setShow} active={active} />
}
